/**
 * Cross-feature selection bus (deliverable 4: "store-as-selection-bus").
 *
 * The Mission board and the ETB timeline are two independent features composed
 * together only inside the Operations workspace route. Neither may import the
 * other. This tiny store is the explicit, testable seam between them: selecting
 * a mission in the board calls {@link MissionSelectionStore.select}; the ETB
 * panel reads {@link MissionSelectionStore.selectedMissionId} reactively.
 *
 * `selectedLogbookId` remains a legacy compatibility field. The operational
 * ETB no longer guesses a logbook from a mission id or asks the operator to
 * type one: the incident-first workspace resolves it through
 * `GET /api/v1/operational-log/incidents/{incidentId}/logbook`. Selecting a
 * mission still clears this compatibility value because only the selected
 * incident is authoritative for M15 hydration.
 *
 * `selectedResponsibleOrganizationUnitId` is a third, independent field carrying
 * the selected mission's `MissionSummary.responsibleOrganizationUnitId` (a field
 * the Mission board row already has — no extra request). It exists so the M10
 * resource-request form (`apps/web/src/features/resource-requests/`, a separate
 * route) can pre-fill its own `missionId` AND `organizationUnitId` fields from
 * this bus rather than requiring the operator to type both. The caller passes it
 * explicitly to `select()` (optional, defaults to `null`) instead of this store
 * reaching into `MissionBoardStore` itself, keeping the two features decoupled —
 * the same seam discipline as `selectedLogbookId` above.
 *
 * Promoted to an app-level store (`AppStores.missionSelection`, constructed once
 * in `createAppStores()`) rather than local `useState` in `operations-route.tsx`,
 * specifically so a selection survives navigating away from `/operations` to the
 * separate `/modules/resource-requests` route — the whole point of deliverable
 * 5(a) is a selection made on one page pre-filling a form on another.
 *
 * `selectedMapObjectId` is a fourth, independent field added for the M14
 * situation-map panel (ADR-010; legacy-assessment M2 "store-as-selection-bus").
 * It deliberately extends THIS store rather than introducing a parallel,
 * disconnected selection store: a map marker click and an accessible-list-mirror
 * row selection are the same user action and must land in one place, exactly
 * like the mission board and ETB panel already share `selectedMissionId`. A new
 * mission selection clears it (a previously selected map object belongs to the
 * mission context that is being replaced, mirroring how `selectedLogbookId` is
 * cleared above); it is otherwise independent of the other three fields.
 */
import { makeAutoObservable } from "mobx";

export class MissionSelectionStore {
  selectedMissionId: string | null = null;
  selectedLogbookId: string | null = null;
  selectedResponsibleOrganizationUnitId: string | null = null;
  selectedMapObjectId: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Called by the Mission board when an operator selects a row. */
  select(missionId: string, responsibleOrganizationUnitId: string | null = null): void {
    this.selectedMissionId = missionId;
    this.selectedLogbookId = null;
    this.selectedResponsibleOrganizationUnitId = responsibleOrganizationUnitId;
    this.selectedMapObjectId = null;
  }

  /**
   * Called by the situation-map store from either selection origin it wires up:
   * a list-mirror row activation, or the `MapEngine`'s `onSelect` callback
   * reporting a marker click. Both origins call this same method, so the map
   * canvas and the accessible list always agree on the current selection.
   */
  selectMapObject(mapObjectId: string | null): void {
    this.selectedMapObjectId = mapObjectId;
  }

  /** Legacy compatibility setter; incident-bound ETB screens use the M15 incident lookup. */
  setLogbookId(logbookId: string | null): void {
    this.selectedLogbookId = logbookId;
  }

  clear(): void {
    this.selectedMissionId = null;
    this.selectedLogbookId = null;
    this.selectedResponsibleOrganizationUnitId = null;
    this.selectedMapObjectId = null;
  }
}
